import { t } from '@transifex/native';

import { flashes } from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as sdk from 'pkg/core/sdk';

export interface AddTeamRequest {
	groupId?: number;
	opponentId?: number;
	playerUserIds?: number[];
	opponentPlayerIds?: number[];
}

export interface TournamentTeamSummary {
	id: number;
	tournamentId: number;
	groupId?: number;
	opponentId?: number;
	displayName?: string;
}

export const addTeams = async (
	organizationID: number,
	tournamentID: number,
	teams: AddTeamRequest[]
): Promise<[Response, TournamentTeamSummary[] | null]> => {
	const request = await sdk.post(
		endpoints.Tournaments.AddTeam(organizationID, tournamentID),
		{},
		{ teams }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show({
			title: t('Could not add teams'),
			message: response?.error,
		});
		return [request, null];
	}

	const created: TournamentTeamSummary[] = await request.json();

	return [request, created];
};

export const removeTeam = async (
	organizationID: number,
	tournamentID: number,
	teamID: number
): Promise<Response> => {
	const request = await sdk.destroy(
		endpoints.Tournaments.RemoveTeam(organizationID, tournamentID, teamID),
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not remove team'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const removeTeams = async (
	organizationID: number,
	tournamentID: number,
	teamIDs: number[]
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.RemoveTeams(organizationID, tournamentID),
		{},
		{ teamIds: teamIDs }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not remove teams'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export interface TimeWindow {
	days: number[];
	startTime: string;
	endTime: string;
}

// Mirrors api ScheduleConstraints — used by both the group-stage and bracket
// schedule endpoints. Empty/omitted means "no scheduling, just create slots".
export interface ScheduleConstraints {
	startDate?: string;
	endDate?: string;
	// IANA timezone name the dates/times are expressed in (e.g. "Europe/Stockholm").
	// The backend builds all slots in this zone and pins it on the tournament.
	timezone?: string;
	gameDays?: number[];
	timeWindows?: TimeWindow[];
	slotMinutes?: number;
	matchFrequency?: 'per_week' | 'per_day' | 'packed';
	blackoutDates?: string[];
	overrideBookings?: boolean;
}

// Result of a schedule generation request. Presentation (inline message vs.
// background toast) is left to the caller — the schedule modal shows the error
// inline, while the fixtures table surfaces it as a flash.
export interface ScheduleResult {
	ok: boolean;
	error?: string;
}

export const generateBracket = async (
	organizationID: number,
	tournamentID: number,
	constraints: ScheduleConstraints = {}
): Promise<ScheduleResult> => {
	const request = await sdk.post(
		endpoints.Tournaments.GenerateBracket(organizationID, tournamentID),
		{},
		constraints
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		return { ok: false, error: response?.error };
	}

	return { ok: true };
};

export const generateFixtures = async (
	organizationID: number,
	tournamentID: number,
	constraints: ScheduleConstraints = {}
): Promise<ScheduleResult> => {
	const request = await sdk.post(
		endpoints.Tournaments.GenerateFixtures(organizationID, tournamentID),
		{},
		constraints
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		return { ok: false, error: response?.error };
	}

	return { ok: true };
};

export interface UpdateFixtureRequest {
	startsAt?: number;
	location?: string;
	timezone?: string;
	resourceId?: number;
}

export const updateFixture = async (
	organizationID: number,
	tournamentID: number,
	gameID: number,
	body: UpdateFixtureRequest
): Promise<Response> => {
	const request = await sdk.patch(
		endpoints.Tournaments.UpdateFixture(organizationID, tournamentID, gameID),
		{},
		body
	);

	// 409 is the team-double-booking guard; the caller words it against the
	// time the user picked instead of echoing the server's English string.
	if (!request.ok && request.status !== 409) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not update match'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const removeFixture = async (
	organizationID: number,
	tournamentID: number,
	gameID: number
): Promise<Response> => {
	const request = await sdk.destroy(
		endpoints.Tournaments.DeleteGame(organizationID, tournamentID, gameID),
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not delete match'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const deleteGames = async (
	organizationID: number,
	tournamentID: number,
	gameIDs: number[]
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.DeleteGames(organizationID, tournamentID),
		{},
		{ gameIds: gameIDs }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not delete matches'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const unscheduleGames = async (
	organizationID: number,
	tournamentID: number,
	gameIDs: number[]
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.UnscheduleGames(organizationID, tournamentID),
		{},
		{ gameIds: gameIDs }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not unschedule matches'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const swapGames = async (
	organizationID: number,
	tournamentID: number,
	gameIDs: [number, number]
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.SwapGames(organizationID, tournamentID),
		{},
		{ gameIds: gameIDs }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not swap matches'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export interface RescheduleGamesRequest {
	gameIds: number[];
	date?: string;
	time?: string;
	resourceId?: number;
}

export const rescheduleGames = async (
	organizationID: number,
	tournamentID: number,
	body: RescheduleGamesRequest
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.RescheduleGames(organizationID, tournamentID),
		{},
		body
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not reschedule matches'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const setTeamVenue = async (
	organizationID: number,
	tournamentID: number,
	tournamentTeamID: number,
	resourceID: number
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.SetTeamVenue(organizationID, tournamentID),
		{},
		{ tournamentTeamId: tournamentTeamID, resourceId: resourceID }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not set home venue'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const scheduleActivation = async (
	organizationID: number,
	tournamentID: number,
	scheduledAt: number
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.ScheduleActivation(organizationID, tournamentID),
		{},
		{ scheduledAt }
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not schedule activation'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const cancelScheduledActivation = async (
	organizationID: number,
	tournamentID: number
): Promise<Response> => {
	const request = await sdk.destroy(
		endpoints.Tournaments.CancelScheduledActivation(
			organizationID,
			tournamentID
		),
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not cancel scheduled activation'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const publishTournament = async (
	organizationID: number,
	tournamentID: number
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.Activate(organizationID, tournamentID),
		{},
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not publish tournament'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const unpublishTournament = async (
	organizationID: number,
	tournamentID: number
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.Unpublish(organizationID, tournamentID),
		{},
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not unpublish tournament'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const removeTournament = async (
	organizationID: number,
	tournamentID: number
): Promise<Response> => {
	const request = await sdk.destroy(
		endpoints.Tournaments.Delete(organizationID, tournamentID),
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not delete tournament'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const removeTeamVenue = async (
	organizationID: number,
	tournamentID: number,
	teamID: number,
	resourceID: number
): Promise<Response> => {
	const request = await sdk.destroy(
		endpoints.Tournaments.RemoveTeamVenue(
			organizationID,
			tournamentID,
			teamID,
			resourceID
		),
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not remove home venue'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};

export const duplicateTournament = async (
	organizationID: number,
	tournamentID: number
): Promise<Response> => {
	const request = await sdk.post(
		endpoints.Tournaments.Duplicate(organizationID, tournamentID),
		{},
		{}
	);

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));
		flashes.show(
			{
				title: t('Could not duplicate tournament'),
				message: response?.error,
			},
			request.status
		);
	}

	return request;
};
